import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type DriverDebtBucket } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import {
  driverDebtAuthority,
  driverDebtPortfolioView,
  driverDebtScreenState,
  formatMarketInstant,
  loadDriverDebts,
  marketTimeZone,
  type DriverDebtPortfolioView,
  type DriverDebtRowView,
} from '../../src/index';
import { useAdmin } from '../session';
import { PersonName } from '../components/person-name';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Notice, PageHeader, Panel, StatCard, Table, EmptyState, Grid, Text, InlineRow } from '@vello/web-theme';

/**
 * Finance › **Carteira de devedores** (W8-FINAL-FARE-AND-CASH, D438; finding A-02) over `GET /v1/finance/driver-debts`
 * (`payout:view`): the drivers of a market who owe the platform commission, largest first, with the debt aged in buckets
 * (0–7, 8–30, 31–60, 61+ days), the oldest dated debt, and a flag when part of it predates the ledger's dated history.
 *
 * - The market is chosen from the operator's scope (the payouts/cash-handovers picker — never typed); nothing is read
 *   without one.
 * - Totals per bucket are exact (bigint minor units, tabular numerals) in the portfolio's one currency.
 * - A refused or failed read is an ERROR explained in words (`MARKET_REQUIRED`, `MARKET_INVALID`,
 *   `PAYOUT_CURRENCY_REQUIRED`, `DEBTS_SOURCE_UNAVAILABLE`, a bare 403) — never an empty portfolio or zero totals.
 * - Read only, and it follows the scope by itself (re-read every 30 s in the background). No native dialog.
 */
export function DriverDebtsScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { t, lang } = useT();
  const { fail, v, refusal: refusalOf } = useVocab();
  const { mayView } = driverDebtAuthority(grants);
  // Owner 2026-10-12: the market IS the header scope's city — no second market selector on this screen.
  const { market: scopeMarket, choices } = useScopeMarket();
  const market = scopeMarket ?? '';
  const [loaded, setLoaded] = useState<string>('');
  const [portfolio, setPortfolio] = useState<DriverDebtPortfolioView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  /** The portfolio shown is the LAST one asked for — a slower answer for another market is dropped. */
  const seq = useRef(0);

  const load = useCallback(
    async (m: string, background = false): Promise<void> => {
      const mine = ++seq.current;
      const target = m.trim();
      // A background tick keeps the portfolio on screen; a new market starts from a clean one.
      if (!background) {
        setLoaded(target);
        setPortfolio(undefined);
        setError(undefined);
      }
      if (target === '' || !mayView) return;
      const res = await loadDriverDebts(runtime, target);
      if (mine !== seq.current) return;
      if (res.ok) {
        setError(undefined);
        setPortfolio(driverDebtPortfolioView(res.value));
      } else {
        setError(refusalOf('refusal.admin.driver_debt', res.error) ?? t('debts.error', { code: fail(res.error) }));
      }
    },
    [runtime, mayView, t],
  );

  // The portfolio follows the header scope's market and stays current in the background (no "load" button).
  useEffect(() => {
    void load(market);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);
  useAutoRefresh(() => load(market, true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' && mayView });

  const currency = portfolio?.currency ?? '';
  const money = (amountMinor: bigint | undefined): string =>
    amountMinor === undefined || !isIso4217Currency(currency) ? '—' : formatMoney({ amountMinor, currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' });
  const tz = marketTimeZone(loaded, markets);
  const state = driverDebtScreenState({ mayView, market: loaded, ...(error !== undefined ? { error } : {}), ...(portfolio !== undefined ? { portfolio } : {}) });
  const note = (text: string): ReactElement => <EmptyState title={text} />;
  const bucketLabel = (b: DriverDebtBucket): string => v('debt.aging_bucket', b);

  return (
    <>
      <PageHeader title={t('debts.title')} subtitle={t('debts.subtitle')} />

      {!mayView ? (
        <Panel>{note(t('debts.noPermission'))}</Panel>
      ) : scopeMarket === undefined ? (
        <ScopeMarketNotice choices={choices} />
      ) : (
        <>
          {error !== undefined && (
            <Panel>
              <Notice tone="danger">{error}</Notice>
            </Panel>
          )}

          {portfolio !== undefined && (
            <>
              <Grid min="sm" gap={4}>
                <StatCard label={t('debts.total')} value={money(portfolio.totalMinor)} hint={t('debts.total.hint', { count: String(portfolio.rows.length) })} />
                {portfolio.buckets.map((b) => (
                  <StatCard key={b} label={bucketLabel(b)} value={money(portfolio.totals[b])} tone={b === 'D61_PLUS' && (portfolio.totals[b] ?? 0n) > 0n ? 'warning' : undefined} />
                ))}
              </Grid>
              <Text as="p" role="caption" tone="tertiary" mono>
                {t('debts.asOf', { when: formatMarketInstant(portfolio.asOf, tz, lang), policy: portfolio.policyVersion })}
              </Text>
              {portfolio.undatedCount > 0 && (
                <div>
                  <Notice tone="warning">{t('debts.undated.notice', { count: String(portfolio.undatedCount) })}</Notice>
                </div>
              )}
            </>
          )}

          <Panel padded={false}>
            <Table
              columns={[
                { key: 'driver', header: t('debts.col.driver'), render: (d: DriverDebtRowView) => <PersonName id={d.driverId} /> },
                { key: 'debt', header: t('debts.col.debt'), numeric: true, render: (d: DriverDebtRowView) => <span className="tnum">{money(d.debtMinor)}</span> },
                ...(portfolio?.buckets ?? []).map((b) => ({
                  key: b,
                  header: bucketLabel(b),
                  numeric: true,
                  render: (d: DriverDebtRowView) => <span className="tnum">{money(d.aging[b])}</span>,
                })),
                {
                  key: 'oldest',
                  header: t('debts.col.oldest'),
                  render: (d: DriverDebtRowView) => (
                    <InlineRow gap={2} align="center" wrap>
                      <span className="tnum">{d.oldestAt !== undefined ? formatMarketInstant(d.oldestAt, tz, lang) : '—'}</span>
                      {d.undatedRemainder && <Badge tone="warning">{t('debts.undated')}</Badge>}
                    </InlineRow>
                  ),
                },
              ]}
              rows={portfolio?.rows ?? []}
              rowKey={(d) => d.driverId}
              loading={state === 'loading'}
              loadingLabel={t('ui.loading')}
              error={state === 'refused' ? t('ui.loadFailed') : undefined}
              onRetry={() => void load(market)}
              retryLabel={t('ui.retry')}
              empty={note(state === 'needs_market' ? t('debts.needsMarket') : t('debts.empty'))}
            />
          </Panel>
        </>
      )}
    </>
  );
}
